import { useQuery } from '@apollo/client/react'

import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

export type LikingIcon = 'heart' | 'star' | 'thumbsUp'

export type ReactionType =
  | 'celebrate'
  | 'external'
  | 'funny'
  | 'insightful'
  | 'like'
  | 'love'
  | 'support'

/**
 * The unified `configuration` endpoint, selecting only what Community consumes
 * today. It maps the response into the Community config domain types below so
 * the CommunityConfig accessors — and their consumers — depend on a stable
 * shape rather than GraphQL internals. The legacy per-channel settings read the
 * channel's `comment` engagement entry only; the rest read across entries,
 * because media support and mention availability both vary by engagement type
 * on one channel.
 *
 * @see CET-2671
 */
export const ConfigurationQuery = graphql(/* GraphQL */ `
  query ConfigurationQuery($input: ConfigurationInput!) {
    configuration(input: $input) {
      channels {
        channelId
        service
        channelType
        engagement {
          engagementType
          media {
            supportsMedia
            disclaimer
            additionalInformation
          }
          syncData {
            syncDataMechanism
          }
          supportedAiFeatures
          reactions {
            requiresAuthorization
            restingIcon
            supportedReactions {
              reactionType
              visual {
                __typename
                ... on IconVisual {
                  icon
                }
                ... on ImageVisual {
                  url
                }
              }
            }
          }
          replying {
            prefixReplyWithMention
          }
          metadata {
            __typename
            ... on GoogleBusinessEngagementMetadata {
              permanentNote
            }
          }
        }
        content {
          configurationContentTypes
          rules {
            __typename
            ... on LengthRule {
              property
              maxLength
            }
          }
        }
        authorizationStatus {
          feature
          status
        }
      }
    }
  }
`)

// Internal raw types derived from the query — used only for mapping.
type RawChannel = NonNullable<
  ResultOf<typeof ConfigurationQuery>['configuration']
>['channels'][number]
type RawEngagement = RawChannel['engagement'][number]
type RawSupportedReaction =
  RawEngagement['reactions']['supportedReactions'][number]

// Scalar types derived from the query — used in the domain types below.
type Service = RawChannel['service']
type ChannelType = RawChannel['channelType']

// Domain types — isolated from the GraphQL query structure. The hook maps raw
// query data into these shapes; consumers never depend on GQL internals.

// A reaction's visual is discriminated on render strategy — a built-in icon vs
// a network-supplied image — never on the network or the reaction identity.
export type IconVisual = {
  type: 'icon'
  icon: LikingIcon
}

export type ImageVisual = {
  type: 'image'
  url: string
}

export type ReactionVisual = IconVisual | ImageVisual

export type SupportedReaction = {
  reactionType: ReactionType
  visual: ReactionVisual
}

export type ReactionConfiguration = {
  supportedReactions: SupportedReaction[]
}

export type ChannelLiking = {
  supportLiking: boolean
  icon?: LikingIcon | null
  reactionsConfiguration?: ReactionConfiguration | null
}

export type ChannelSettings = {
  supportedChannel: { service: Service; type: ChannelType }
  syncType?: string | null
  mentionType?: string | null
  supportAIFeatures?: boolean | null
  liking?: ChannelLiking | null
  permanentNote?: string | null
}

/**
 * Whether media on an engagement type reaches Buffer, and the copy explaining
 * it when it doesn't. `disclaimer` and `additionalInformation` are populated
 * only when `supportsMedia` is false.
 */
export type MediaConfiguration = {
  supportsMedia: boolean
  disclaimer: string | null
  additionalInformation: string | null
}

/**
 * One channel's media configuration for one engagement type. Keyed by
 * `channelId` rather than service + channelType: Instagram's `facebookLogin`
 * and `instagramLogin` variants differ on media support while sharing both.
 */
export type ChannelMediaSupport = {
  channelId: string
  engagementType: RawEngagement['engagementType']
  media: MediaConfiguration
}

export type ChannelCharacterLimit = {
  id: string
  limit: number
}

export type ChannelAuthorizationStatus = {
  id: string
  accessTo: Array<{ feature: string; status: string }>
}

/**
 * Whether liking is blocked on one channel + engagement type until the channel
 * reconnects with the reaction scope. Keyed by both: the requirement varies per
 * engagement type, while the granted scope is per channel.
 */
export type ChannelReactionAuthorization = {
  channelId: string
  engagementType: RawEngagement['engagementType']
  needsAuthorization: boolean
}

/**
 * The reactions a channel offers, per engagement type. Separate from
 * `ChannelLiking`, which reshapes the comment entry alone into the legacy
 * `commentsConfiguration` structure and so cannot answer for a mention.
 */
export type EngagementReactions = {
  restingIcon: LikingIcon
  supportedReactions: SupportedReaction[]
}

export type ChannelReactionSupport = EngagementReactions & {
  channelId: string
  engagementType: RawEngagement['engagementType']
}

export type CommunityConfigurationData = {
  channelsSettings: ChannelSettings[] | null
  channelCharacterLimits: ChannelCharacterLimit[] | null
  channelAuthorizationStatus: ChannelAuthorizationStatus[] | null
  channelReactionAuthorization: ChannelReactionAuthorization[] | null
  channelReactionSupport: ChannelReactionSupport[] | null
  channelMediaSupport: ChannelMediaSupport[] | null
  /**
   * Ids of the channels that can surface mentions. Keyed by channel rather than
   * by service + type because support varies per channel: Instagram Login
   * channels share their service + type with Facebook Login ones but have no
   * mention surface, and the org's mentions rollout is applied per service.
   */
  mentionsSupportedChannelIds: string[] | null
  /**
   * Ids of the channels whose `mention` feature is blocked until the channel
   * reconnects. Only statuses a reconnect can clear count — see
   * RECONNECTABLE_STATUSES.
   */
  mentionsNeedsRefreshChannelIds: string[] | null
  error: string | null
  loading: boolean
}

// The legacy endpoint was comment-only, so every field maps from the channel's
// `comment` engagement entry.
function getCommentEngagement(channel: RawChannel): RawEngagement | undefined {
  return channel.engagement.find((e) => e.engagementType === 'comment')
}

function mapSupportedReaction(raw: RawSupportedReaction): SupportedReaction {
  if (raw.visual.__typename === 'ImageVisual') {
    return {
      reactionType: raw.reactionType,
      visual: { type: 'image', url: raw.visual.url },
    }
  }
  if (raw.visual.__typename === 'IconVisual') {
    return {
      reactionType: raw.reactionType,
      visual: { type: 'icon', icon: raw.visual.icon },
    }
  }
  // Unknown visual kind (schema grew a member) — fall back to the resting icon.
  return {
    reactionType: raw.reactionType,
    visual: { type: 'icon', icon: 'heart' },
  }
}

function mapLiking(engagement: RawEngagement): ChannelLiking {
  const { reactions } = engagement
  const reactionsConfiguration: ReactionConfiguration = {
    supportedReactions: reactions.supportedReactions.map(mapSupportedReaction),
  }
  return {
    // Empty `supportedReactions` = reacting via Buffer isn't supported.
    supportLiking: reactions.supportedReactions.length > 0,
    icon: reactions.restingIcon,
    reactionsConfiguration,
  }
}

function mapPermanentNote(engagement: RawEngagement): string | null {
  if (engagement.metadata?.__typename === 'GoogleBusinessEngagementMetadata') {
    return engagement.metadata.permanentNote
  }
  return null
}

function mapChannelSettings(channel: RawChannel): ChannelSettings | null {
  const comment = getCommentEngagement(channel)
  // No comment engagement entry = channel isn't supported for comments, so it
  // doesn't belong in `channelsSettings` (the legacy "supported channels" set).
  if (!comment) return null

  return {
    supportedChannel: {
      service: channel.service,
      type: channel.channelType,
    },
    // The accessor treats webhook/firehose as realtime; the new endpoint
    // collapses both into `realTime`. Map back to a value the accessor reads.
    syncType:
      comment.syncData.syncDataMechanism === 'realTime' ? 'webhook' : 'polling',
    // Legacy `mentionType === Text` gated seeding the reply with the author's
    // @mention; the new `prefixReplyWithMention` (read from the comment entry to
    // preserve current behaviour) encodes exactly that.
    mentionType: comment.replying.prefixReplyWithMention ? 'text' : null,
    supportAIFeatures: comment.supportedAiFeatures.includes('suggestions'),
    liking: mapLiking(comment),
    permanentNote: mapPermanentNote(comment),
  }
}

function mapCharacterLimit(channel: RawChannel): ChannelCharacterLimit | null {
  const commentContent = channel.content.find((content) =>
    content.configurationContentTypes.includes('comment'),
  )
  const lengthRule = commentContent?.rules.find(
    (rule) => rule.__typename === 'LengthRule' && rule.property === 'text',
  )
  if (!lengthRule || lengthRule.__typename !== 'LengthRule') return null
  return { id: channel.channelId, limit: lengthRule.maxLength }
}

function supportsMentions(channel: RawChannel): boolean {
  return channel.engagement.some((e) => e.engagementType === 'mention')
}

function mapAuthorizationStatus(
  channel: RawChannel,
): ChannelAuthorizationStatus {
  return {
    id: channel.channelId,
    // Filter to the `comment` feature to preserve legacy behaviour — the old
    // endpoint only exposed the comments feature's status.
    accessTo: channel.authorizationStatus
      .filter((auth) => auth.feature === 'comment')
      .map((auth) => ({ feature: auth.feature, status: auth.status })),
  }
}

/**
 * The statuses a reconnect clears. `notEnoughRights` needs an administrator of
 * the platform account, and `needsUpgrade` needs a newer client, so neither is
 * worth a reconnect prompt.
 * @see AuthorizationStatus in graphql/src/comment/queries.graphql
 */
const RECONNECTABLE_STATUSES: ReadonlySet<
  RawChannel['authorizationStatus'][number]['status']
> = new Set(['needsRefresh', 'notEnoughData'])

/**
 * Whether the channel must reconnect before mentions flow. Only the `mention`
 * feature's status counts: a comment or posting refresh says nothing about
 * mentions. A missing entry means the channel doesn't gate mentions on
 * authorization, so it must not prompt a reconnect.
 */
function mentionsNeedRefresh(channel: RawChannel): boolean {
  const status = channel.authorizationStatus.find(
    (auth) => auth.feature === 'mention',
  )?.status
  return status !== undefined && RECONNECTABLE_STATUSES.has(status)
}

function mapReactionAuthorization(
  channel: RawChannel,
): ChannelReactionAuthorization[] {
  // An absent `engagementReaction` entry means the feature is not
  // authorization-gated on this channel. Treating unknown as blocked would
  // prompt a reconnect on channels that can already like.
  const status = channel.authorizationStatus.find(
    (auth) => auth.feature === 'engagementReaction',
  )?.status
  const reconnectClearsIt =
    status !== undefined && RECONNECTABLE_STATUSES.has(status)

  return channel.engagement.map((engagement) => ({
    channelId: channel.channelId,
    engagementType: engagement.engagementType,
    needsAuthorization:
      engagement.reactions.requiresAuthorization && reconnectClearsIt,
  }))
}

/**
 * Reads every engagement entry, because the reaction set differs per engagement
 * type on the same channel — LinkedIn offers six on a comment and the same six
 * on a mention, while X offers only `like`.
 */
function mapReactionSupport(channel: RawChannel): ChannelReactionSupport[] {
  return channel.engagement.map((engagement) => ({
    channelId: channel.channelId,
    engagementType: engagement.engagementType,
    restingIcon: engagement.reactions.restingIcon,
    supportedReactions:
      engagement.reactions.supportedReactions.map(mapSupportedReaction),
  }))
}

/**
 * Unlike the mappers above — legacy accessors that read the `comment` entry
 * only — this reads every engagement entry, because media support differs per
 * engagement type on the same channel.
 */
function mapMediaSupport(channel: RawChannel): ChannelMediaSupport[] {
  return channel.engagement.map((engagement) => ({
    channelId: channel.channelId,
    engagementType: engagement.engagementType,
    media: {
      supportsMedia: engagement.media.supportsMedia,
      disclaimer: engagement.media.disclaimer,
      additionalInformation: engagement.media.additionalInformation,
    },
  }))
}

/**
 * Fetches the unified `configuration` endpoint and maps it into the Community
 * config domain shape consumed by `CommunityConfig`.
 */
export function useConfigurationQuery(): CommunityConfigurationData {
  const organizationId = useCurrentOrganization()?.id ?? ''

  const { data, loading, error } = useQuery(ConfigurationQuery, {
    variables: {
      input: { organizationId },
    },
    fetchPolicy: 'cache-first',
    skip: !organizationId,
  })

  if (loading)
    return {
      channelsSettings: null,
      channelCharacterLimits: null,
      channelAuthorizationStatus: null,
      channelReactionAuthorization: null,
      channelReactionSupport: null,
      channelMediaSupport: null,
      mentionsSupportedChannelIds: null,
      mentionsNeedsRefreshChannelIds: null,
      error: null,
      loading: true,
    }

  const channels = data?.configuration?.channels ?? null

  return {
    channelsSettings:
      channels
        ?.map(mapChannelSettings)
        .filter((setting): setting is ChannelSettings => setting !== null) ??
      null,
    channelCharacterLimits:
      channels
        ?.map(mapCharacterLimit)
        .filter((limit): limit is ChannelCharacterLimit => limit !== null) ??
      null,
    channelAuthorizationStatus: channels?.map(mapAuthorizationStatus) ?? null,
    channelReactionAuthorization:
      channels?.flatMap(mapReactionAuthorization) ?? null,
    channelReactionSupport: channels?.flatMap(mapReactionSupport) ?? null,
    channelMediaSupport: channels?.flatMap(mapMediaSupport) ?? null,
    mentionsSupportedChannelIds:
      channels?.filter(supportsMentions).map((channel) => channel.channelId) ??
      null,
    mentionsNeedsRefreshChannelIds:
      channels
        ?.filter(mentionsNeedRefresh)
        .map((channel) => channel.channelId) ?? null,
    error: error ? 'Error fetching configuration' : null,
    loading,
  }
}
